// NIP-01 profile (kind 0) fetch + cache for the signed-in user.
//
// The header avatar wants a real PFP, not a 2-char pubkey stub. We
// resolve `picture`, `name`, `display_name`, and `about` from kind 0
// metadata by fanning a one-shot REQ across READ_PROFILE_RELAYS, taking
// the first relay that returns an event before EOSE / 15s timeout.
//
// Pattern mirrors /Users/bowz/nostr-surf/packages/frontend/src/lib/relay-fetcher.ts:275-323
// (fetchProfilesFromRelays + fetchOneShot): bare WebSocket, REQ + EOSE,
// no SimplePool (SimplePool keeps long-lived subs we'd then have to tear
// down).
//
// Cache: localStorage under `gives.profile.<pubkey>` with a 24h TTL.
// `loadCachedProfile()` is synchronous for the first paint;
// `fetchProfile()` is fire-and-forget after sign-in to warm the cache.

import { READ_PROFILE_RELAYS } from '@nostr-gives/shared';

export interface NostrProfile {
  name?: string;
  display_name?: string;
  picture?: string;
  about?: string;
}

interface CachedProfile {
  profile: NostrProfile;
  fetchedAt: number;
}

const CACHE_TTL_MS = 24 * 60 * 60 * 1000; // 24h positive cache
const NEGATIVE_TTL_MS = 60 * 60 * 1000; // 1h "no profile anywhere" cache
const FETCH_TIMEOUT_MS = 15_000;
const KEY_PREFIX = 'gives.profile.';
const NEG_KEY_PREFIX = 'gives.profile.miss.';

function cacheKey(pubkey: string): string {
  return `${KEY_PREFIX}${pubkey}`;
}

function negCacheKey(pubkey: string): string {
  return `${NEG_KEY_PREFIX}${pubkey}`;
}

/**
 * In-memory positive cache. Survives the page lifetime; avoids the
 * JSON.parse cost of localStorage on every leaderboard re-render.
 */
const memProfileCache = new Map<string, NostrProfile>();

/**
 * In-memory negative cache. Records pubkeys for which the relay fan-out
 * returned no kind 0 event - prevents re-hammering the relays for the
 * same dead pubkey on every leaderboard scroll / re-paint. Cleared after
 * NEGATIVE_TTL_MS so a freshly-published profile is picked up within
 * an hour.
 */
const memNegativeCache = new Map<string, number>(); // pubkey -> fetchedAt

/**
 * Pubkeys currently being fetched - shared between this module and the
 * leaderboard caller. We dedupe at this layer so two callers asking for
 * the same pubkey in the same tick share one network round-trip.
 */
const inFlight = new Map<string, Promise<NostrProfile | null>>();

/**
 * Synchronous read. Consults the in-memory map first (zero JSON parse
 * cost) and falls back to localStorage. Returns null if missing OR if
 * the cached entry is older than {@link CACHE_TTL_MS}. Caller is
 * responsible for kicking off `fetchProfile()` to refresh.
 */
export function loadCachedProfile(pubkey: string): NostrProfile | null {
  const mem = memProfileCache.get(pubkey);
  if (mem) return mem;
  try {
    const raw = localStorage.getItem(cacheKey(pubkey));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<CachedProfile>;
    if (
      !parsed ||
      typeof parsed.fetchedAt !== 'number' ||
      !parsed.profile ||
      typeof parsed.profile !== 'object'
    ) {
      return null;
    }
    if (Date.now() - parsed.fetchedAt > CACHE_TTL_MS) {
      return null;
    }
    memProfileCache.set(pubkey, parsed.profile);
    return parsed.profile;
  } catch {
    return null;
  }
}

function saveCachedProfile(pubkey: string, profile: NostrProfile): void {
  memProfileCache.set(pubkey, profile);
  try {
    const entry: CachedProfile = { profile, fetchedAt: Date.now() };
    localStorage.setItem(cacheKey(pubkey), JSON.stringify(entry));
  } catch {
    // ignore - storage may be full or disabled
  }
}

/**
 * True when we recently tried to fetch this profile and got nothing
 * back from any relay. Used by the leaderboard hydrator to skip
 * obviously-unresolvable pubkeys without paying for the WebSocket
 * fan-out on every re-render. Backed by localStorage so the negative
 * answer also survives page reloads for {@link NEGATIVE_TTL_MS}.
 */
export function isProfileMissCached(pubkey: string): boolean {
  const mem = memNegativeCache.get(pubkey);
  if (typeof mem === 'number') {
    if (Date.now() - mem <= NEGATIVE_TTL_MS) return true;
    memNegativeCache.delete(pubkey);
  }
  try {
    const raw = localStorage.getItem(negCacheKey(pubkey));
    if (!raw) return false;
    const ts = Number(raw);
    if (!Number.isFinite(ts)) return false;
    if (Date.now() - ts > NEGATIVE_TTL_MS) {
      localStorage.removeItem(negCacheKey(pubkey));
      return false;
    }
    memNegativeCache.set(pubkey, ts);
    return true;
  } catch {
    return false;
  }
}

function rememberProfileMiss(pubkey: string): void {
  const ts = Date.now();
  memNegativeCache.set(pubkey, ts);
  try {
    localStorage.setItem(negCacheKey(pubkey), String(ts));
  } catch {
    // ignore
  }
}

interface NostrKind0Event {
  kind: number;
  pubkey: string;
  content: string;
  created_at: number;
}

/**
 * One-shot fetch: connect, REQ, collect events until EOSE, close. 15s
 * timeout. Resolves with the highest created_at event (NIP-01 says newer
 * kind 0 wins).
 */
function fetchKind0OneShot(
  relayUrl: string,
  pubkey: string,
): Promise<NostrKind0Event | null> {
  return new Promise((resolve) => {
    let ws: WebSocket;
    try {
      ws = new WebSocket(relayUrl);
    } catch {
      resolve(null);
      return;
    }
    const subId = `prof_${Math.random().toString(36).slice(2, 10)}`;
    let best: NostrKind0Event | null = null;
    let settled = false;

    const finish = (): void => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      try {
        ws.send(JSON.stringify(['CLOSE', subId]));
      } catch {
        // ignore
      }
      try {
        ws.close();
      } catch {
        // ignore
      }
      resolve(best);
    };

    const timer = setTimeout(finish, FETCH_TIMEOUT_MS);

    ws.onopen = (): void => {
      try {
        ws.send(
          JSON.stringify(['REQ', subId, { authors: [pubkey], kinds: [0], limit: 1 }]),
        );
      } catch {
        finish();
      }
    };
    ws.onmessage = (e: MessageEvent): void => {
      try {
        const msg = JSON.parse(typeof e.data === 'string' ? e.data : '');
        if (!Array.isArray(msg)) return;
        if (msg[0] === 'EVENT' && msg[1] === subId) {
          const ev = msg[2] as NostrKind0Event | undefined;
          if (ev && typeof ev.content === 'string' && ev.kind === 0) {
            if (!best || (ev.created_at ?? 0) > (best.created_at ?? 0)) {
              best = ev;
            }
          }
        } else if (msg[0] === 'EOSE' && msg[1] === subId) {
          finish();
        }
      } catch {
        // ignore - garbled relay message
      }
    };
    ws.onerror = (): void => {
      finish();
    };
    ws.onclose = (): void => {
      finish();
    };
  });
}

function parseProfileContent(content: string): NostrProfile | null {
  try {
    const parsed = JSON.parse(content) as Record<string, unknown>;
    if (!parsed || typeof parsed !== 'object') return null;
    const out: NostrProfile = {};
    if (typeof parsed.name === 'string') out.name = parsed.name;
    if (typeof parsed.display_name === 'string') out.display_name = parsed.display_name;
    if (typeof parsed.picture === 'string') out.picture = parsed.picture;
    if (typeof parsed.about === 'string') out.about = parsed.about;
    return out;
  } catch {
    return null;
  }
}

/**
 * Fan out kind-0 fetches across READ_PROFILE_RELAYS in parallel, take
 * the newest event (by created_at) across all relays that responded
 * before the 15s window, cache it, and return the parsed profile.
 *
 * Returns null only if EVERY relay timed out / errored or no kind 0 was
 * found at all. Callers should treat null as "leave the fallback stub
 * up" rather than as a hard error.
 */
export async function fetchProfile(pubkey: string): Promise<NostrProfile | null> {
  if (!pubkey || pubkey.startsWith('anon_') || pubkey.startsWith('stub_')) {
    // Anonymous + stub sessions have no real pubkey; skip the network.
    return null;
  }
  // Hot-path short-circuits: already cached positive, already cached
  // negative within TTL, or another caller is mid-flight for the same
  // pubkey. Crucial for the leaderboard, which calls fetchProfile per
  // row on every paint and could otherwise hammer relays.
  const cached = memProfileCache.get(pubkey);
  if (cached) return cached;
  if (isProfileMissCached(pubkey)) return null;
  const existing = inFlight.get(pubkey);
  if (existing) return existing;

  const job = (async (): Promise<NostrProfile | null> => {
    const results = await Promise.all(
      READ_PROFILE_RELAYS.map((url) => fetchKind0OneShot(url, pubkey)),
    );
    let best: NostrKind0Event | null = null;
    for (const ev of results) {
      if (!ev) continue;
      if (!best || (ev.created_at ?? 0) > (best.created_at ?? 0)) {
        best = ev;
      }
    }
    if (!best) {
      rememberProfileMiss(pubkey);
      return null;
    }
    const profile = parseProfileContent(best.content);
    if (!profile) {
      rememberProfileMiss(pubkey);
      return null;
    }
    saveCachedProfile(pubkey, profile);
    return profile;
  })();
  inFlight.set(pubkey, job);
  try {
    return await job;
  } finally {
    inFlight.delete(pubkey);
  }
}
